/**
 * Analytics Hook for Tennis Gourmet
 * 
 * Provides a comprehensive analytics interface for tracking user behavior
 * and business insights. Automatically respects cookie consent and provides
 * fallback tracking for when analytics are disabled.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from '@tanstack/react-router';
import { 
  trackLocalBusinessEvent,
  trackSectionView,
  trackMenuInteraction,
  trackContactFormEvent,
  trackReservationInterest,
  trackSocialClick,
  trackGalleryInteraction,
  trackScrollDepth,
  trackPerformanceMetrics
} from '~/components/GoogleAnalytics';
import { cookieManager, CookieCategory } from '~/lib/cookie-manager';

// Analytics event types for type safety
export interface AnalyticsEvent {
  category: 'user_engagement' | 'business_conversion' | 'technical_insights' | 'content_interaction';
  action: string;
  label?: string;
  value?: number;
  customParameters?: Record<string, unknown>;
}

// Business-specific event types
export type BusinessEventType = 
  | 'menu_view'
  | 'reservation_inquiry'
  | 'contact_form_submission'
  | 'phone_click'
  | 'email_click'
  | 'social_media_click'
  | 'gallery_interaction'
  | 'section_engagement';

export function useAnalytics() {
  const location = useLocation();
  const pageStartTime = useRef<number>(Date.now());
  const scrollDepthTracked = useRef<Set<number>>(new Set());
  const sectionViewTimes = useRef<Map<string, number>>(new Map());

  // Check if analytics are enabled (for development logging only)
  const isAnalyticsEnabled = useCallback(() => {
    return cookieManager.isCategoryConsented(CookieCategory.ANALYTICS);
  }, []);

  // Generic event tracking (now relies on Consent Mode v2)
  const trackEvent = useCallback((event: AnalyticsEvent) => {
    // With Consent Mode v2, we can always call tracking functions
    // GA will automatically respect consent settings
    trackLocalBusinessEvent(event.action, {
      event_category: event.category,
      event_label: event.label,
      value: event.value,
      ...event.customParameters
    });

    // Optional: Log to console in development for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log('Analytics event tracked:', event, 'Consent:', isAnalyticsEnabled());
    }
  }, [isAnalyticsEnabled]);

  // Business-specific event tracking
  const trackBusinessEvent = useCallback((
    eventType: BusinessEventType,
    details?: Record<string, unknown>
  ) => {
    const eventMap: Record<BusinessEventType, AnalyticsEvent> = {
      menu_view: {
        category: 'content_interaction',
        action: 'menu_viewed',
        label: details?.menuSection as string,
        customParameters: details
      },
      reservation_inquiry: {
        category: 'business_conversion',
        action: 'reservation_interest',
        value: 75, // Estimated lead value
        customParameters: details
      },
      contact_form_submission: {
        category: 'business_conversion',
        action: 'contact_form_completed',
        value: 100, // High value conversion
        customParameters: details
      },
      phone_click: {
        category: 'business_conversion',
        action: 'phone_number_clicked',
        value: 50,
        customParameters: details
      },
      email_click: {
        category: 'business_conversion',
        action: 'email_clicked',
        value: 30,
        customParameters: details
      },
      social_media_click: {
        category: 'user_engagement',
        action: 'social_media_clicked',
        customParameters: details
      },
      gallery_interaction: {
        category: 'content_interaction',
        action: 'gallery_engaged',
        customParameters: details
      },
      section_engagement: {
        category: 'user_engagement',
        action: 'section_viewed',
        customParameters: details
      }
    };

    const event = eventMap[eventType];
    if (event) {
      trackEvent(event);
    }
  }, [trackEvent]);

  // Track page views with enhanced data
  const trackPageView = useCallback((customData?: Record<string, unknown>) => {
    const pageData = {
      page_title: document.title,
      page_location: window.location.href,
      page_path: location.pathname,
      business_section: getBusinessSection(location.pathname),
      user_type: getUserType(),
      device_type: getDeviceType(),
      ...customData
    };

    trackLocalBusinessEvent('page_view', pageData);

    // Optional: Log to console in development for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log('Page view tracked:', pageData, 'Consent:', isAnalyticsEnabled());
    }
  }, [location.pathname, isAnalyticsEnabled]);

  // Track time spent on page
  const trackPageTimeSpent = useCallback(() => {
    if (!isAnalyticsEnabled()) return;

    const timeSpent = Math.round((Date.now() - pageStartTime.current) / 1000);
    
    if (timeSpent > 10) { // Only track if user spent meaningful time
      trackLocalBusinessEvent('page_engagement', {
        time_spent_seconds: timeSpent,
        page_path: location.pathname,
        engagement_quality: getEngagementQuality(timeSpent)
      });
    }
  }, [location.pathname, isAnalyticsEnabled]);

  // Track scroll depth
  const trackScrollProgress = useCallback((percentage: number) => {
    if (!isAnalyticsEnabled()) return;

    // Track at 25%, 50%, 75%, 100% milestones
    const milestones = [25, 50, 75, 100];
    const milestone = milestones.find(m => percentage >= m && !scrollDepthTracked.current.has(m));
    
    if (milestone) {
      scrollDepthTracked.current.add(milestone);
      trackScrollDepth(milestone, location.pathname);
    }
  }, [location.pathname, isAnalyticsEnabled]);

  // Track section visibility
  const trackSectionVisibility = useCallback((sectionName: string, isVisible: boolean) => {
    if (!isAnalyticsEnabled()) return;

    if (isVisible) {
      sectionViewTimes.current.set(sectionName, Date.now());
    } else {
      const startTime = sectionViewTimes.current.get(sectionName);
      if (startTime) {
        const timeSpent = Math.round((Date.now() - startTime) / 1000);
        if (timeSpent > 3) { // Only track if viewed for more than 3 seconds
          trackSectionView(sectionName, timeSpent);
        }
        sectionViewTimes.current.delete(sectionName);
      }
    }
  }, [isAnalyticsEnabled]);

  // Track performance metrics
  const trackPerformance = useCallback(() => {
    if (typeof window === 'undefined') return;

    // Use Performance API if available
    if ('performance' in window && 'getEntriesByType' in performance) {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

      if (navigation) {
        const metrics = {
          pageLoadTime: Math.round(navigation.loadEventEnd - navigation.fetchStart),
          deviceType: getDeviceType(),
          connectionSpeed: getConnectionSpeed()
        };

        trackPerformanceMetrics(metrics);

        // Optional: Log to console in development for debugging
        if (process.env.NODE_ENV === 'development') {
          console.log('Performance metrics tracked:', metrics, 'Consent:', isAnalyticsEnabled());
        }
      }
    }
  }, [isAnalyticsEnabled]);

  // Reset tracking state when page changes
  useEffect(() => {
    pageStartTime.current = Date.now();
    scrollDepthTracked.current.clear();
    sectionViewTimes.current.clear();
  }, [location.pathname]);

  // Track page view on route change
  useEffect(() => {
    trackPageView();
  }, [trackPageView]);

  // Track page time spent on unmount or route change
  useEffect(() => {
    return () => {
      trackPageTimeSpent();
    };
  }, [trackPageTimeSpent]);

  // Track performance metrics on page load
  useEffect(() => {
    const timer = setTimeout(trackPerformance, 2000); // Wait for page to fully load
    return () => clearTimeout(timer);
  }, [trackPerformance]);

  return {
    // Core tracking functions
    trackEvent,
    trackBusinessEvent,
    trackPageView,
    trackScrollProgress,
    trackSectionVisibility,
    
    // Specific business tracking
    trackMenuView: (menuSection: string) => trackBusinessEvent('menu_view', { menuSection }),
    trackReservationInquiry: (type: 'restaurant' | 'sports', method: string) => 
      trackBusinessEvent('reservation_inquiry', { type, method }),
    trackContactSubmission: (formData: Record<string, unknown>) => 
      trackBusinessEvent('contact_form_submission', formData),
    trackPhoneClick: (location: string) => trackBusinessEvent('phone_click', { location }),
    trackEmailClick: (location: string) => trackBusinessEvent('email_click', { location }),
    trackSocialClick: (platform: string, location: string) => 
      trackBusinessEvent('social_media_click', { platform, location }),
    trackGalleryInteraction: (action: string, details?: Record<string, unknown>) => 
      trackBusinessEvent('gallery_interaction', { action, ...details }),
    
    // Utility functions
    isAnalyticsEnabled,
    
    // Enhanced tracking with business context
    trackUserJourney: (step: string, data?: Record<string, unknown>) => {
      trackEvent({
        category: 'user_engagement',
        action: 'user_journey_step',
        label: step,
        customParameters: { journey_step: step, ...data }
      });
    }
  };
}

// Helper functions
function getBusinessSection(pathname: string): string {
  if (pathname.includes('restaurant')) return 'restaurant';
  if (pathname.includes('sport')) return 'sports';
  if (pathname.includes('about')) return 'about';
  if (pathname.includes('contact')) return 'contact';
  return 'home';
}

function getUserType(): 'new' | 'returning' {
  // Simple heuristic based on localStorage
  const hasVisited = localStorage.getItem('tg_has_visited');
  if (!hasVisited) {
    localStorage.setItem('tg_has_visited', 'true');
    return 'new';
  }
  return 'returning';
}

function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

function getConnectionSpeed(): 'slow' | 'medium' | 'fast' {
  // Use Network Information API if available
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    if (connection.effectiveType) {
      if (connection.effectiveType.includes('2g')) return 'slow';
      if (connection.effectiveType.includes('3g')) return 'medium';
      return 'fast';
    }
  }
  return 'medium'; // Default assumption
}

function getEngagementQuality(timeSpent: number): 'low' | 'medium' | 'high' {
  if (timeSpent < 30) return 'low';
  if (timeSpent < 120) return 'medium';
  return 'high';
}

export default useAnalytics;
